import {
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent,
} from "react";
import { ArrowIcon } from "./icons";
import { useLocale } from "./locale";
import { Link } from "./navigation";
import {
  READER_ROUTE_STEP_COUNT,
  readerMaterialHref,
  readerMaterialKey,
  readerRouteStep,
  resolveReaderMaterial,
  type ReaderMaterialReference,
} from "./readerRoutes";
import {
  clearReaderState,
  getReaderStateServerSnapshot,
  getReaderStateSnapshot,
  recordReaderStep,
  subscribeReaderState,
  toggleSavedReaderMaterial,
} from "./readerState";

const continuityCopy = {
  ru: {
    kicker: "Ваше чтение",
    title: "Продолжить с того места, где вы остановились",
    localNote: "Прогресс и сохранённые материалы остаются только в этом браузере. Регистрация не нужна.",
    route: {
      seeing: "Лучше видеть реальность",
      decision: "Принять трудное решение",
      limits: "Встретиться с пределом",
    },
    currentStep: (step: number) => `Шаг ${step} из ${READER_ROUTE_STEP_COUNT}`,
    opened: (count: number) => `Открыто ${count} из ${READER_ROUTE_STEP_COUNT}`,
    continue: "Продолжить чтение",
    next: "Следующий шаг",
    saved: "Сохранённые материалы",
    savedShort: (count: number) => `Сохранено · ${count}`,
    remove: "Убрать",
    save: "Сохранить",
    savedAction: "Сохранено",
    savedStatus: "Материал сохранён на этом устройстве.",
    removedStatus: "Материал удалён из сохранённых.",
    clear: "Очистить прогресс и сохранённое",
    clearQuestion: "Очистить данные чтения на этом устройстве?",
    clearConfirm: "Да, очистить",
    cancel: "Отмена",
    cleared: "Прогресс и сохранённые материалы удалены с этого устройства.",
    nextKicker: "Ваш маршрут",
    nextTitle: "Следующий ясный шаг",
    routeReturn: "Вернуться к трём маршрутам",
    routeReviewed: "Вы открыли практику этого маршрута. Можно вернуться к карте и выбрать следующий вход.",
  },
  uk: {
    kicker: "Ваше читання",
    title: "Продовжити з місця, де ви зупинилися",
    localNote: "Прогрес і збережені матеріали залишаються лише в цьому браузері. Реєстрація не потрібна.",
    route: {
      seeing: "Краще бачити реальність",
      decision: "Ухвалити складне рішення",
      limits: "Зустрітися з межею",
    },
    currentStep: (step: number) => `Крок ${step} із ${READER_ROUTE_STEP_COUNT}`,
    opened: (count: number) => `Відкрито ${count} із ${READER_ROUTE_STEP_COUNT}`,
    continue: "Продовжити читання",
    next: "Наступний крок",
    saved: "Збережені матеріали",
    savedShort: (count: number) => `Збережено · ${count}`,
    remove: "Прибрати",
    save: "Зберегти",
    savedAction: "Збережено",
    savedStatus: "Матеріал збережено на цьому пристрої.",
    removedStatus: "Матеріал вилучено зі збережених.",
    clear: "Очистити прогрес і збережене",
    clearQuestion: "Очистити дані читання на цьому пристрої?",
    clearConfirm: "Так, очистити",
    cancel: "Скасувати",
    cleared: "Прогрес і збережені матеріали видалено з цього пристрою.",
    nextKicker: "Ваш маршрут",
    nextTitle: "Наступний зрозумілий крок",
    routeReturn: "Повернутися до трьох маршрутів",
    routeReviewed: "Ви відкрили практику цього маршруту. Можна повернутися до карти й обрати наступний вхід.",
  },
  en: {
    kicker: "Your reading",
    title: "Continue where you left off",
    localNote: "Progress and saved materials stay only in this browser. No account is required.",
    route: {
      seeing: "See reality more clearly",
      decision: "Make a difficult decision",
      limits: "Meet a limit",
    },
    currentStep: (step: number) => `Step ${step} of ${READER_ROUTE_STEP_COUNT}`,
    opened: (count: number) => `Opened ${count} of ${READER_ROUTE_STEP_COUNT}`,
    continue: "Continue reading",
    next: "Next step",
    saved: "Saved materials",
    savedShort: (count: number) => `Saved · ${count}`,
    remove: "Remove",
    save: "Save",
    savedAction: "Saved",
    savedStatus: "Material saved on this device.",
    removedStatus: "Material removed from saved items.",
    clear: "Clear progress and saved items",
    clearQuestion: "Clear reading data on this device?",
    clearConfirm: "Yes, clear it",
    cancel: "Cancel",
    cleared: "Progress and saved materials were removed from this device.",
    nextKicker: "Your route",
    nextTitle: "The next clear step",
    routeReturn: "Return to the three routes",
    routeReviewed: "You opened this route’s practice. You can return to the map and choose another entry point.",
  },
} as const;

export function useReaderState() {
  return useSyncExternalStore(
    subscribeReaderState,
    getReaderStateSnapshot,
    getReaderStateServerSnapshot,
  );
}

export function ReaderContinuityPanel() {
  const { language, chapterMap, essayMap } = useLocale();
  const state = useReaderState();
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [actionStatus, setActionStatus] = useState("");
  const savedHeadingRef = useRef<HTMLHeadingElement>(null);
  const clearedStatusRef = useRef<HTMLParagraphElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const c = continuityCopy[language];
  const resumeMaterial = state.resume
    ? resolveReaderMaterial(state.resume.material, chapterMap, essayMap)
    : null;
  const savedMaterials = state.saved.flatMap((material) => {
    const resolved = resolveReaderMaterial(material, chapterMap, essayMap);
    return resolved ? [{ material, resolved }] : [];
  });

  if (!state.resume && savedMaterials.length === 0 && !cleared) return null;

  const removeSaved = (
    material: ReaderMaterialReference,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    const item = event.currentTarget.closest("li");
    const nextButton = item?.nextElementSibling?.querySelector<HTMLButtonElement>("button")
      ?? item?.previousElementSibling?.querySelector<HTMLButtonElement>("button");
    toggleSavedReaderMaterial(material);
    setActionStatus(c.removedStatus);
    window.requestAnimationFrame(() => (nextButton ?? savedHeadingRef.current)?.focus());
  };

  const clearLocalReading = () => {
    setConfirmingClear(false);
    setCleared(true);
    clearReaderState();
    window.requestAnimationFrame(() => clearedStatusRef.current?.focus());
  };

  const cancelClear = () => {
    setConfirmingClear(false);
    window.requestAnimationFrame(() => clearButtonRef.current?.focus());
  };

  const routeState = state.resume ? state.routes[state.resume.routeId] : null;
  const nextMaterial = state.resume
    ? readerRouteStep(state.resume.routeId, state.resume.stepIndex + 1)
    : null;
  const nextResolved = nextMaterial
    ? resolveReaderMaterial(nextMaterial, chapterMap, essayMap)
    : null;

  return (
    <section id="reader-continuity" className="reader-continuity" aria-labelledby="reader-continuity-title">
      <header className="reader-continuity-heading">
        <div>
          <p className="section-kicker">{c.kicker}</p>
          <h3 id="reader-continuity-title">{c.title}</h3>
        </div>
        <p>{c.localNote}</p>
      </header>

      {cleared && !state.resume && savedMaterials.length === 0 && (
        <p ref={clearedStatusRef} className="reader-cleared-status" role="status" tabIndex={-1}>
          {c.cleared}
        </p>
      )}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{actionStatus}</p>

      {state.resume && resumeMaterial && routeState && (
        <div className="reader-resume">
          <div className="reader-resume-progress">
            <span>{c.route[state.resume.routeId]}</span>
            <strong>{c.currentStep(state.resume.stepIndex)} · {c.opened(routeState.openedSteps.length)}</strong>
            <progress
              max={READER_ROUTE_STEP_COUNT}
              value={routeState.openedSteps.length}
              aria-label={c.opened(routeState.openedSteps.length)}
            />
          </div>
          <div className="reader-resume-material">
            <strong>{resumeMaterial.title}</strong>
            <div className="reader-continuity-actions">
              <Link
                className="reader-continuity-primary"
                href={readerMaterialHref(state.resume.material, { resume: true })}
              >
                {c.continue} <ArrowIcon />
              </Link>
              {nextMaterial && nextResolved && (
                <Link
                  href={readerMaterialHref(nextMaterial)}
                  onClick={() => recordReaderStep(
                    state.resume!.routeId,
                    state.resume!.stepIndex + 1,
                    nextMaterial,
                  )}
                >
                  {c.next}: {nextResolved.title}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {savedMaterials.length > 0 && (
        <div className="reader-saved">
          <h4 ref={savedHeadingRef} tabIndex={-1}>{c.saved}</h4>
          <ul>
            {savedMaterials.map(({ material, resolved }) => (
              <li key={readerMaterialKey(material)}>
                <Link href={readerMaterialHref(material)}>{resolved.title}</Link>
                <button
                  type="button"
                  onClick={(event) => removeSaved(material, event)}
                  aria-label={`${c.remove}: ${resolved.title}`}
                >
                  {c.remove}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(state.resume || savedMaterials.length > 0) && (
        <div
          className="reader-clear"
          role={confirmingClear ? "group" : undefined}
          aria-label={confirmingClear ? c.clearQuestion : undefined}
        >
          {confirmingClear ? (
            <>
              <span>{c.clearQuestion}</span>
              <button type="button" onClick={clearLocalReading}>{c.clearConfirm}</button>
              <button type="button" onClick={cancelClear}>{c.cancel}</button>
            </>
          ) : (
            <button ref={clearButtonRef} type="button" onClick={() => setConfirmingClear(true)}>{c.clear}</button>
          )}
        </div>
      )}
    </section>
  );
}

export function SaveMaterialButton({
  material,
  title,
}: {
  material: ReaderMaterialReference;
  title: string;
}) {
  const { language } = useLocale();
  const state = useReaderState();
  const [status, setStatus] = useState("");
  const c = continuityCopy[language];
  const saved = state.saved.some((candidate) => (
    readerMaterialKey(candidate) === readerMaterialKey(material)
  ));

  const toggle = () => {
    toggleSavedReaderMaterial(material);
    setStatus(saved ? c.removedStatus : c.savedStatus);
  };

  return (
    <span className="save-material-wrap">
      <button
        type="button"
        className="save-material"
        aria-pressed={saved}
        aria-label={`${saved ? c.savedAction : c.save}: ${title}`}
        onClick={toggle}
      >
        {saved ? c.savedAction : c.save}
      </button>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">{status}</span>
    </span>
  );
}

export function ReaderRouteNextStep({ material }: { material: ReaderMaterialReference }) {
  const { language, chapterMap, essayMap } = useLocale();
  const state = useReaderState();
  const c = continuityCopy[language];
  if (
    !state.resume
    || readerMaterialKey(state.resume.material) !== readerMaterialKey(material)
  ) return null;

  const nextMaterial = readerRouteStep(state.resume.routeId, state.resume.stepIndex + 1);
  const nextResolved = nextMaterial
    ? resolveReaderMaterial(nextMaterial, chapterMap, essayMap)
    : null;

  return (
    <aside className="reader-route-next" aria-labelledby={`reader-route-next-${readerMaterialKey(material).replaceAll(":", "-")}`}>
      <p className="section-kicker">{c.nextKicker} · {c.currentStep(state.resume.stepIndex)}</p>
      <h3 id={`reader-route-next-${readerMaterialKey(material).replaceAll(":", "-")}`}>{c.nextTitle}</h3>
      {nextMaterial && nextResolved ? (
        <Link
          href={readerMaterialHref(nextMaterial)}
          onClick={() => recordReaderStep(
            state.resume!.routeId,
            state.resume!.stepIndex + 1,
            nextMaterial,
          )}
        >
          <span>{c.currentStep(state.resume.stepIndex + 1)}</span>
          <strong>{nextResolved.title}</strong>
          <ArrowIcon />
        </Link>
      ) : (
        <>
          <p>{c.routeReviewed}</p>
          <Link href="/#reader-routes">
            <strong>{c.routeReturn}</strong>
            <ArrowIcon />
          </Link>
        </>
      )}
    </aside>
  );
}

export function SavedMaterialsLink() {
  const { language } = useLocale();
  const state = useReaderState();
  if (state.saved.length === 0) return null;
  return <Link href="/#reader-continuity">{continuityCopy[language].savedShort(state.saved.length)}</Link>;
}
